"use strict";

/**
 * Quantity limit feedback for buy-form quantity selectors.
 *
 * When `settings.limit_quantity` caps the quantity input at the available stock, the selector
 * silently disables the plus button and clamps keyboard entry with no feedback. This adds:
 *   - Persistent mode (settings.qty_limit_message on): an "Only X available" message below the
 *     selector when stock is at/below the threshold, which animates when the limit is hit.
 *   - Temporary mode (off): a transient warning shown only when the customer tries to exceed the
 *     limit, auto-dismissing after a few seconds.
 *
 * Markup hook: `[data-qty-limit]` inside `.product-quantity-box` (rendered by quantity-box.liquid
 * for buy-forms only). Listeners are document-delegated and a single MutationObserver tracks the
 * input's `max` attribute, so dynamically injected forms (quick-shop, featured-product) work too.
 */
(function () {
  window.PXUTheme = window.PXUTheme || {};

  if (window.PXUTheme._qtyLimitLoaded) {
    return;
  }
  window.PXUTheme._qtyLimitLoaded = true;

  var WARNING_DURATION = 3000;

  function settings() {
    return window.PXUTheme.theme_settings || {};
  }

  function translation() {
    return window.PXUTheme.translation || {};
  }

  function persistentEnabled() {
    var value = settings().qty_limit_message;
    return value === true || value === "true";
  }

  function getMax(input) {
    var max = parseInt(input.getAttribute("max"), 10);
    return isNaN(max) ? null : max;
  }

  function messageText(count) {
    var template = translation().only_available_html || "Only [[count]] available";
    return template.replace("[[count]]", count);
  }

  function animationClasses(feedback) {
    var anim =
      feedback.getAttribute("data-qty-limit-animation") || settings().qty_limit_animation || "shake_colour";
    var classes = [];
    if (anim === "shake" || anim === "shake_colour") {
      classes.push("is-shake");
    }
    if (anim === "colour" || anim === "shake_colour") {
      classes.push("is-colour");
    }
    return classes;
  }

  function clearAnimation(feedback) {
    feedback.classList.remove("is-shake", "is-colour");
  }

  function applyAnimation(feedback) {
    var classes = animationClasses(feedback);
    clearAnimation(feedback);
    // Force a reflow so the animation restarts on repeated triggers.
    void feedback.offsetWidth;
    for (var i = 0; i < classes.length; i++) {
      feedback.classList.add(classes[i]);
    }
  }

  function getThreshold(feedback) {
    var threshold = parseInt(feedback.getAttribute("data-qty-limit-threshold"), 10);
    if (isNaN(threshold)) {
      threshold = parseInt(settings().qty_limit_message_threshold, 10);
    }
    return isNaN(threshold) ? 0 : threshold;
  }

  function parts(box) {
    if (!box) {
      return null;
    }
    var feedback = box.querySelector("[data-qty-limit]");
    var input = box.querySelector(".quantity-input");
    if (!feedback || !input) {
      return null;
    }
    return {
      feedback: feedback,
      input: input,
      text: feedback.querySelector("[data-qty-limit-text]")
    };
  }

  /* Persistent message: show "Only X available" when stock is at/below the threshold. */
  function syncMessage(box) {
    var p = parts(box);
    if (!p || !persistentEnabled()) {
      return;
    }

    var max = getMax(p.input);
    var threshold = getThreshold(p.feedback);

    if (max !== null && max > 0 && max <= threshold) {
      if (p.text) {
        p.text.innerHTML = messageText(max);
      }
      p.feedback.classList.add("is-persistent", "is-visible");
    } else {
      if (p.text) {
        p.text.innerHTML = "";
      }
      p.feedback.classList.remove("is-persistent", "is-visible");
    }
  }

  /* Limit reached: animate the persistent message, or flash a temporary warning. */
  function triggerFeedback(box) {
    var p = parts(box);
    if (!p) {
      return;
    }

    var max = getMax(p.input);
    if (max === null || max <= 0) {
      return;
    }

    if (p.text) {
      p.text.innerHTML = messageText(max);
    }
    p.feedback.classList.add("is-visible");

    if (persistentEnabled()) {
      p.feedback.classList.add("is-persistent");
      applyAnimation(p.feedback);
      return;
    }

    applyAnimation(p.feedback);
    if (p.feedback._qtyHideTimer) {
      clearTimeout(p.feedback._qtyHideTimer);
    }
    p.feedback._qtyHideTimer = setTimeout(function () {
      // Remove is-visible to let the CSS collapse/fade the message out smoothly.
      // Keep the text in place so it stays readable through the transition; it is
      // overwritten on the next trigger.
      p.feedback.classList.remove("is-visible");
      clearAnimation(p.feedback);
    }, WARNING_DURATION);
  }

  function boxFor(target) {
    if (!target || !target.closest) {
      return null;
    }
    var box = target.closest(".product-quantity-box");
    if (!box || !box.querySelector("[data-qty-limit]")) {
      return null;
    }
    return box;
  }

  /* Customer clicks the plus button while already at the cap. */
  function onClick(event) {
    if (!event.target.closest) {
      return;
    }
    var plus = event.target.closest(".quantity-plus");
    if (!plus) {
      return;
    }
    var box = boxFor(plus);
    if (!box) {
      return;
    }
    var input = box.querySelector(".quantity-input");
    var max = getMax(input);
    var value = parseInt(input.value, 10);
    if (max !== null && !isNaN(value) && value >= max) {
      triggerFeedback(box);
    }
  }

  /* Customer types a value above the available stock. */
  function onInput(event) {
    var input = event.target;
    if (!input.classList || !input.classList.contains("quantity-input")) {
      return;
    }
    var box = boxFor(input);
    if (!box) {
      return;
    }
    var max = getMax(input);
    var value = parseInt(input.value, 10);
    if (max !== null && !isNaN(value) && value > max) {
      triggerFeedback(box);
    }
  }

  function onAnimationEnd(event) {
    if (!event.target.closest) {
      return;
    }
    var feedback = event.target.closest("[data-qty-limit]");
    if (!feedback) {
      return;
    }
    // Remove only the class whose animation just ended. The shake (wrapper, qtyShake)
    // finishes before the colour pulse (inner text, qtyColour); clearing both on the
    // first animationend would cut the colour effect short under the shake_colour setting.
    if (event.animationName === "qtyShake") {
      feedback.classList.remove("is-shake");
    } else if (event.animationName === "qtyColour") {
      feedback.classList.remove("is-colour");
    } else {
      clearAnimation(feedback);
    }
  }

  function syncNode(node) {
    if (node.nodeType !== 1) {
      return;
    }
    if (node.matches && node.matches("[data-qty-limit]")) {
      syncMessage(node.closest(".product-quantity-box"));
    }
    if (node.querySelectorAll) {
      var found = node.querySelectorAll("[data-qty-limit]");
      for (var i = 0; i < found.length; i++) {
        syncMessage(found[i].closest(".product-quantity-box"));
      }
    }
  }

  function syncAll() {
    var nodes = document.querySelectorAll("[data-qty-limit]");
    for (var i = 0; i < nodes.length; i++) {
      syncMessage(nodes[i].closest(".product-quantity-box"));
    }
  }

  function init() {
    // Capture phase so this runs BEFORE the theme's body-level increment handler
    // (utilities.js), letting onClick read the pre-click value. Otherwise a normal
    // click that lands exactly on max would falsely register as hitting the cap.
    document.addEventListener("click", onClick, true);
    document.addEventListener("input", onInput);
    document.addEventListener("animationend", onAnimationEnd);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (
          mutation.type === "attributes" &&
          mutation.target.classList &&
          mutation.target.classList.contains("quantity-input")
        ) {
          syncMessage(mutation.target.closest(".product-quantity-box"));
        } else if (mutation.type === "childList") {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            syncNode(mutation.addedNodes[j]);
          }
        }
      }
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["max"]
    });

    syncAll();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
